<i18n>
[
    "product__addToBox--label",
    "product__addedToBox--label",
    "product__unavailable--message",
    "product__prepurchase--tagComponentText",
]
</i18n>

<template>
    <ProductInfoContainer class="c-product-info">
        <div
            v-if="isBuyMode"
            class="c-product-info__badge-container u-spacer--3"
        >
            <Badge
                :badgeText="$t('product__prepurchase--tagComponentText')"
                icon="account--promo"
                :bkgColor="BADGE_COLORS.sunshine"
                color="black"
            />
        </div>
        <ProductInfoHeader
            class="u-spacer--3"
            :productDetails="productDetails"
            :product="selectedChoice"
            :isBuyMode="isBuyMode"
        />
        <ProductPrepurchaseInfo
            v-if="isBuyMode"
            class="u-spacer--3"
            :prePurchaseProduct="selectedChoice?.prePurchase"
        />
        <ProductSelector
            v-if="useBoxColorSelector"
            class="u-spacer--4"
            :selectedChoice="selectedChoice"
            :colorChoices="colorChoices"
            :sizeCodeAvailibilityByChoiceId="sizeCodeAvailibilityByChoiceId"
            :colorCode="colorCode"
            :reviewSummary="productDetails?.reviewSummary"
        />
        <template v-else>
            <ProductColorSelectorPanel
                class="u-spacer--2"
                :displayName="selectedChoice.color?.displayName"
            >
                <ProductColorSelector
                    :choices="colorChoices"
                    :selectedChoice="selectedChoice"
                    :sizeCodeAvailibilityByChoiceId="sizeCodeAvailibilityByChoiceId"
                />
            </ProductColorSelectorPanel>
        </template>
        <template v-if="productDetails.isAvailable">
            <template v-if="!useBoxColorSelector">
                <ProductSizeSelector
                    :class="{
                        'u-spacer--1': productDetails?.reviewSummary?.fitRatingCount,
                        'u-spacer--2': !productDetails?.reviewSummary?.fitRatingCount,
                    }"
                    :selectedChoice="selectedChoice"
                    :colorCode="colorCode"
                    :sizeCodeAvailibilityByChoiceId="sizeCodeAvailibilityByChoiceId"
                />
                <ProductFitSliderScale
                    v-if="productDetails?.reviewSummary?.fitRatingCount"
                    class="u-spacer--4"
                    :reviewSummary="productDetails.reviewSummary"
                />
            </template>
            <PrePurchaseBuyButton
                v-if="isBuyMode"
                :colorCode="colorCode"
                :selectedChoice="selectedChoice"
                class="u-spacer--4"
            />
            <template v-else>
                <ProductActionButtons
                    :selectedChoice="selectedChoice"
                    class="u-spacer--4"
                />
                <ContentfulContentBlock
                    v-if="contentfulTopContent"
                    :contentRows="contentfulTopContent"
                    class="c-product-info__content-block u-spacer--4"
                />
            </template>
        </template>
        <p
            v-else
            :class="[
                'c-product-info__product-unavailable-text',
                'o-text--caption',
                'u-text--gray'
            ]"
        >
            {{ $t('product__unavailable--message') }}
        </p>
        <ProductBuyDetails
            v-if="isBuyMode"
            :colorCode="colorCode"
            :productAnalyticsData="productAnalyticsData"
        />
        <ProductDetails
            class="u-spacer--3"
            :productDetails="productDetails"
            :colorCode="colorCode"
        />
        <template #noPaddingOnMobile>
            <PDPReviewImagesTray v-if="imageCount" />
            <PDPPrePurchaseTray
                v-if="showPrePurchaseTray"
                class="u-spacer--5"
                :selectedChoice="selectedChoice"
                :productDetails="productDetails"
                :prePurchaseEligibleChoices="prePurchaseEligibleChoices"
            />
        </template>
    </ProductInfoContainer>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import runtimeConfig from '~config/config';
import { BADGE_COLORS } from '~coreModules/core/js/badge-constants';

import Badge from '~coreModules/core/components/ui/Badge.vue';
import ProductInfoContainer from '~coreRoutes/product/components/ProductInfoContainer.vue';
import PDPReviewImagesTray from '~routes/product/components/PDPReviewImagesTray.vue';
import ProductInfoHeader from '~routes/product/components/ProductInfoHeader.vue';
import ProductFitSliderScale from '~routes/product/components/ProductFitSliderScale.vue';
import ProductDetails from '~routes/product/components/ProductDetails.vue';
import ProductActionButtons from '~routes/product/components/ProductActionButtons.vue';
import PrePurchaseBuyButton from '~routes/product/components/PrePurchaseBuyButton.vue';
import ProductSizeSelector from '~routes/product/components/ProductSizeSelector.vue';
import ProductColorSelector from '~routes/product/components/ProductColorSelector.vue';
import ProductColorSelectorPanel from '~routes/product/components/ProductColorSelectorPanel.vue';
import ProductBuyDetails from '~routes/product/components/ProductBuyDetails.vue';
import ProductPrepurchaseInfo from '~routes/product/components/ProductPrePurchaseInfo.vue';
import PDPPrePurchaseTray from '~routes/product/components/PDPPrePurchaseTray.vue';
import ProductSelector from '~routes/product/components/ProductSelector.vue';
import ContentfulContentBlock from '~modules/contentful/components/ContentfulContentBlock.vue';

const props = defineProps({
    productDetails: {
        type: Object,
        required: true,
    },
    prePurchaseEligibleChoices: {
        type: Array,
        default: () => [],
    },
    selectedChoice: {
        type: Object,
        required: true,
    },
    colorCode: {
        type: String,
        required: true,
    },
    isBuyMode: {
        type: Boolean,
        default: false,
    },
    productAnalyticsData: {
        type: Object,
        required: true,
    },
    isLoggedIn: {
        type: Boolean,
        default: false,
    },
    contentfulTopContent: {
        type: Array,
        default: null,
    },
});

const { features } = runtimeConfig;

const sizeCodeAvailibilityByChoiceId = ref({});

const colorChoices = computed(() => (props.isBuyMode ?
    props.prePurchaseEligibleChoices : props.productDetails?.choices));

const imageCount = computed(() => props.productDetails?.reviewSummary?.imageCount || 0);

const showPrePurchaseTray = computed(() => !!props.prePurchaseEligibleChoices?.length &&
    !props.isBuyMode && props.isLoggedIn);

// NU_FEATURE: USE_BOX_COLOR_SELECTOR
// https://urbnit.atlassian.net/browse/TYP-27174
const useBoxColorSelector = computed(() => features.useBoxColorSelector);

// NU_FEATURE: USE_BOX_COLOR_SELECTOR
// https://urbnit.atlassian.net/browse/TYP-27174
// assign sizeCodeAvailibilityByChoiceId in ProductSelector instead of passing as prop
watch(() => colorChoices, (newColorChoices) => {
    sizeCodeAvailibilityByChoiceId.value = (newColorChoices.value || [])
        .reduce((result, { choiceId, sizeGroups }) => {
            const sizeCodes = (sizeGroups || [])
                .flatMap(({ includedSkus }) => includedSkus
                    .filter(({ availableInventory }) => availableInventory > 0)
                    .map(({ size }) => size.code),
                );
            // eslint-disable-next-line no-param-reassign
            result[choiceId] = sizeCodes || [];
            return result;
        }, {});
}, { immediate: true, deep: true });

</script>

<style lang="scss">
    .c-product-info {
        &__content-block {
            padding: $nu-spacer-4 $nu-spacer-2 0;
            background-color: $nu-white;
            margin-left: -$nu-spacer-2;
            width: calc(100% + $nu-spacer-4);
            border-radius: 2px;

            @include breakpoint(medium) {
                width: 100%;
                margin-left: 0;
            }
        }

        &__product-unavailable-text {
            margin: $nu-spacer-3 0 $nu-spacer-6;
        }

        &__badge-container {
            display: none;

            @include breakpoint(medium) {
                display: block;
            }
        }
    }
</style>
